/**
 * Orientation-driven dock decision for the operations panel (#186, extends #131).
 *
 * The panel surface has two macro layouts:
 *   • LANDSCAPE — the interactive map/grid dock: the protected queue column sits
 *     BESIDE the bounded media grid (1/2/grid tiles).
 *   • PORTRAIT  — the reading/list dock (the PEOC portrait-monitor seat): the
 *     protected queues stack at the TOP as reading/list surfaces over a
 *     single-column media reel below.
 *
 * Which one shows is driven by the panel surface's OWN measured dimensions — the
 * SAME ResizeObserver (`useElementSize`) that already drives the width-adaptive
 * queue sub-layout (`queueLayoutForWidth`) — NOT a `matchMedia` / device-orientation
 * guess. One measured source of truth means the dock is correct for any window or
 * embedding, not just a maximized top-level window whose viewport happens to equal
 * the physical screen.
 *
 * Pure (no React) so the threshold is unit-testable.
 */

export type PanelDockMode = 'portrait' | 'landscape';

/**
 * Pick the dock mode for a measured panel size.
 *
 * Portrait when the surface is at least as tall as it is wide (`height >= width`) —
 * matching the CSS `(orientation: portrait)` semantics this replaces (CSS portrait
 * is height ≥ width), so a square-or-taller panel reads as portrait and only a
 * wider-than-tall panel stays landscape (landscape behaviour unchanged).
 *
 * Before the first measurement (`width <= 0` / `height <= 0`) default to LANDSCAPE —
 * the common desktop case, matching the wide default the queue layout uses; the
 * ResizeObserver corrects to portrait within a frame on a tall window, so there is
 * no sticky wrong state.
 */
export function dockModeForSize(width: number, height: number): PanelDockMode {
  if (width <= 0 || height <= 0) return 'landscape';
  return height >= width ? 'portrait' : 'landscape';
}
